import { ArrowLeft } from "lucide-mui";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import type { MouseEvent, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

export interface BackLink {
  to: string;
  label: string;
  /** Runs before navigating; `preventDefault()` cancels (unsaved-changes guards). */
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

interface BackBreadcrumbsProps {
  back: BackLink;
  current?: ReactNode;
  gutterBottom?: boolean;
}

/** "← Parent / Current" trail above a detail page's title. */
export function BackBreadcrumbs({ back, current, gutterBottom = true }: BackBreadcrumbsProps) {
  return (
    <Breadcrumbs sx={{ mb: gutterBottom ? 2 : 0 }}>
      <Link
        component={RouterLink}
        to={back.to}
        onClick={back.onClick}
        underline="hover"
        color="inherit"
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        <ArrowLeft fontSize="small" />
        {back.label}
      </Link>
      {current != null && (
        <Typography
          sx={{
            color: "text.primary",
            maxWidth: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {current}
        </Typography>
      )}
    </Breadcrumbs>
  );
}

interface PageHeaderProps {
  title: ReactNode;
  /** Secondary text (or chips, links) under the title. */
  description?: ReactNode;
  /** Right-aligned: buttons, menus, a status chip. */
  actions?: ReactNode;
  back?: BackLink;
}

export default function PageHeader({ title, description, actions, back }: PageHeaderProps) {
  return (
    <Box sx={{ mb: 3 }}>
      {back && <BackBreadcrumbs back={back} current={title} />}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ mb: description ? 0.5 : 0 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" component="div" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{actions}</Box>
        )}
      </Box>
    </Box>
  );
}
